import React from 'react';

// ============================================
// SLOT CARD - Compact slot display with clients
// ============================================
const SlotCard = ({ slot, bookings, clients, workouts, onAddClient, onRemoveClient, onEdit, onDelete, bulkMode, isSelected, onToggleSelect, onClientClick }) => {
  const slotBookings = bookings.filter(b => b.time_slot_id === slot.id && b.status !== 'cancelled');
  const clientCount = slotBookings.length;
  const capacity = slot.max_clients || 6;

  // Get client name for booking
  const getClientName = (booking) => {
    const client = clients.find(c => c.id === booking.client_id);
    return client?.full_name || 'Unknown client';
  };

  // Check if booking has a linked workout
  const hasWorkout = (booking) => {
    return workouts.some(w => w.booking_id === booking.id);
  };

  return (
    <div
      className={`bg-slate-800/50 rounded-lg p-3 border transition-all ${
        isSelected
          ? 'border-purple-400 bg-purple-900/30'
          : clientCount >= capacity
            ? 'border-amber-500/30'
            : 'border-slate-600/30 hover:border-blue-400/50'
      }`}
      onClick={() => bulkMode && onToggleSelect()}
    >
      {/* Header: Time & Capacity */}
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-2">
          {bulkMode && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => {}}
              className="w-4 h-4 rounded"
            />
          )}
          <span className="text-white font-semibold text-sm">
            {slot.start_time} – {slot.end_time}
          </span>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
          clientCount >= capacity
            ? 'bg-amber-500/20 text-amber-300'
            : 'bg-blue-500/20 text-blue-300'
        }`}>
          {clientCount} / {capacity}
        </span>
      </div>

      {/* Client List with Workout Indicator and Remove Button */}
      {slotBookings.length > 0 && (
        <div className="space-y-1 mb-3">
          {slotBookings.map(booking => {
            const workoutLinked = hasWorkout(booking);
            return (
              <div
                key={booking.id}
                className="flex items-center justify-between text-sm text-slate-300 pl-2 border-l-2 border-blue-400/30 group"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClientClick(booking);
                  }}
                  className="flex items-center gap-1.5 hover:text-blue-300 transition-colors text-left"
                  title={workoutLinked ? "Workout assigned - Click to open planner" : "No workout - Click to open planner"}
                >
                  <span className={workoutLinked ? "text-emerald-400" : "text-amber-400"}>
                    {workoutLinked ? "🏋️" : "⚠️"}
                  </span>
                  <span>{getClientName(booking)}</span>
                </button>
                {!bulkMode && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onRemoveClient(booking); }}
                    className="text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity px-1"
                    title="Remove client"
                  >
                    ✕
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Actions */}
      {!bulkMode && (
        <div className="flex gap-2 pt-2 border-t border-slate-600/30">
          <button
            onClick={(e) => { e.stopPropagation(); onAddClient(slot); }}
            className="flex-1 text-xs text-blue-300 hover:text-blue-200 hover:bg-blue-500/20 py-1 px-2 rounded transition"
            disabled={clientCount >= capacity}
          >
            + Client
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(slot); }}
            className="text-xs text-amber-300 hover:text-amber-200 hover:bg-amber-500/20 py-1 px-2 rounded transition"
          >
            Edit
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(slot); }}
            className="text-xs text-red-400 hover:text-red-300 hover:bg-red-500/20 py-1 px-2 rounded transition"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

export default SlotCard;
